import { TaskName } from '@/common/constants/taskNames.constants';
import { TaskState } from '@schema';
import i18n from '@/common/i18n';
import { store } from '@/common/root';
import type { TaskMap } from '@/common/store/modules/tasks/tasksNotificationMapper';

const taskMap: TaskMap = {
  [TaskName.NFS_NODE_ASSIGN]: {
    [TaskState.SUCCESS]: ({ payload }) => {
      const nodesList = payload.nodes_list;
      const isUninstalled = store.getters['nfsCluster/isUninstalled'];
      const node = store.getters['locations/getNodeById'](nodesList[0].id);

      if (!node) return undefined;

      const notifyDataWithCluster = {
        title: i18n.tc('Node added to NFS storage|Nodes added to NFS storage', nodesList.length),
        message: i18n.tc(
          'Node "{name}" successfully added to NFS storage|{count} nodes successfully added to NFS storage',
          nodesList.length,
          { name: node.name, count: nodesList.length }
        )
      };
      const notifyDataWithoutCluster = {
        title: i18n.t('NFS storage created'),
        message: i18n.t('NFS storage successfully created')
      };

      return isUninstalled ? notifyDataWithoutCluster : notifyDataWithCluster;
    },
    [TaskState.RUNNING]: ({ payload }) => {
      const isUninstalled = store.getters['nfsCluster/isUninstalled'];
      const nodesList = payload.nodes_list;
      const node = store.getters['locations/getNodeById'](nodesList[0].id);

      const message = isUninstalled
        ? i18n.t('Creating NFS storage...')
        : i18n.tc(
          'Adding node "{name}" to NFS storage...|Adding {count} nodes to NFS storage...',
          nodesList.length,
          { name: node.name, count: nodesList.length }
        );

      return {
        message
      };
    },
    [TaskState.FAILED]: ({ error, payload }) => {
      const isUninstalled = store.getters['nfsCluster/isUninstalled'];
      const nodesList = payload.nodes_list;

      return {
        title: isUninstalled
          ? i18n.t('Error creating NFS storage')
          : i18n.tc('Error adding node to NFS storage|Error adding nodes to NFS storage', nodesList.length),
        message: error
      };
    }
  },
  [TaskName.NFS_SHARE_ADD]: {
    [TaskState.SUCCESS]: ({ payload }) => ({
      title: i18n.t('Share created'),
      message: i18n.t('NFS share "{0}" successfully created', [payload.name]),
    }),
    [TaskState.RUNNING]: ({ payload }) => ({
      message: i18n.t('Creating NFS share "{0}"...', [payload.name])
    }),
    [TaskState.FAILED]: ({ error }) => ({
      title: i18n.t('Error creating share'),
      message: error
    })
  },
  [TaskName.NFS_SHARE_UPDATE]: {
    [TaskState.SUCCESS]: ({ pathVariables }) => {
      const [_clusterId, shareName] = pathVariables || [];
      return {
        title: i18n.t('NFS share updated'),
        message: i18n.t('NFS share "{0}" successfully updated', [shareName])
      };
    },
    [TaskState.RUNNING]: ({ pathVariables }) => {
      const [_clusterId, shareName] = pathVariables || [];
      return {
        message: i18n.t('Updating NFS share "{0}"...', [shareName])
      };
    },
    [TaskState.FAILED]: ({ error }) => ({
      title: i18n.t('Error updating NFS share'),
      message: error
    })
  },
  [TaskName.NFS_EXPORT_ADD]: {
    [TaskState.SUCCESS]: ({ payload }) => ({
      title: i18n.t('NFS share export created'),
      message: i18n.t('Export "{0}" successfully created', [payload.export_name])
    }),
    [TaskState.RUNNING]: ({ payload }) => ({
      message: i18n.t('Creating NFS share export "{0}"...', [payload.export_name])
    }),
    [TaskState.FAILED]: ({ error }) => ({
      title: i18n.t('Error creating NFS share export'),
      message: error
    })
  },
  [TaskName.NFS_EXPORT_UPDATE]: {
    [TaskState.SUCCESS]: ({ pathVariables }) => {
      const [_clusterId, _shareId, exportName] = pathVariables || [];
      return {
        title: i18n.t('NFS share export updated'),
        message: i18n.t('Export "{0}" successfully updated', [exportName])
      };
    },
    [TaskState.RUNNING]: ({ pathVariables }) => {
      const [_clusterId, _shareId, exportName] = pathVariables || [];
      return {
        message: i18n.t('Updating NFS share export "{0}"...', [exportName])
      };
    },
    [TaskState.FAILED]: ({ error }) => ({
      title: i18n.t('Error updating NFS share export'),
      message: error
    })
  }
};

export default taskMap;
